// src/pages/ProductDetails.js
import React, { useState, useEffect, useContext } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, updateDoc, setDoc, deleteDoc, serverTimestamp, runTransaction, collection, addDoc, query, where, orderBy, getDocs } from "firebase/firestore";
import { auth, db } from "../utils/firebaseconfig";
import { CartContext } from "../utils/cartContext";
import "../styles/productDetails.css";
import { checkVerification } from "../utils/verificationCheck";

function ProductDetails() {
  const { productId } = useParams();
  const { addToCart } = useContext(CartContext); 
  const location = useLocation();
  const navigate = useNavigate();
  const [product, setProduct] = useState(location.state?.product || null);
  const [reviews, setReviews] = useState([]);
  const [reviewText, setReviewText] = useState("");
  const [selectedRating, setSelectedRating] = useState(5);
  const [averageRating, setAverageRating] = useState(product?.rating || 0);
  const [user, setUser] = useState(null);
  const [usernames, setUsernames] = useState({});
  const [liked, setLiked] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [hasReviewed, setHasReviewed] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isVerified, setIsVerified] = useState(user?.emailVerified || false);

  useEffect(() => {
    onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        checkIfLiked(currentUser.uid);
        setIsVerified(currentUser.emailVerified);
      }
    });
  }, []);

  // Check if the current user has already reviewed this product
  useEffect(() => {
    if (user && reviews.length > 0) {
      const userReview = reviews.find(review => review.userId === user.uid);
      setHasReviewed(!!userReview);
    }
  }, [user, reviews]);

  const checkIfLiked = async (uid) => {
    try {
      const likeDoc = await getDoc(doc(db, "Product", productId, "Likes", uid));
      setLiked(likeDoc.exists());
    } catch (error) {
      console.error("Error checking like status:", error);
    }
  };

  const handleLikeToggle = async () => {
    if (!user) {
      alert("You must be logged in to like/unlike a product.");
      return;
    }
    try {
      const likeRef = doc(db, "Product", productId, "Likes", user.uid);
      if (liked) {
        await deleteDoc(likeRef);
        setLiked(false);
      } else {
        await setDoc(likeRef, { likedAt: new Date().toISOString() });
        setLiked(true);
      }
    } catch (error) {
      console.error("Error toggling like:", error);
    }
  };

  const handleAddToCart = () => {
    // Check if user is verified
    if (user && !user.emailVerified) {
      if (!checkVerification(navigate)) {
        return;
      }
    }
    
    console.log("Adding to cart:", product);
    const imageUrl = Array.isArray(product.images) && product.images[0] ? product.images[0] : "https://placehold.co/300";
    const cartItem = {
      id: product.id,
      title: product.title || product.name,
      price: Number(product.price) || 0,
      image: imageUrl
    };
    
    const added = addToCart(cartItem);
    
    if (added) {
      // Optional: add success feedback to the user
      console.log("Product added to cart successfully");
    } else {
      if (user && !user.emailVerified) {
        // Verification issue
        console.log("Please verify your email before adding products to cart");
      } else {
        // Some other issue
        console.log("Failed to add product to cart");
      }
    }
  };

  const fetchProduct = async () => {
    try {
      console.log("Fetching product details for ID:", productId);
      const productRef = doc(db, "Product", productId);
      const productDoc = await getDoc(productRef);
      
      if (productDoc.exists()) {
        const data = productDoc.data();
        console.log("Product data retrieved:", data);
        
        // Ensure data structure is consistent
        const productData = {
          id: productDoc.id,
          ...data,
          reviews: Array.isArray(data.reviews) ? data.reviews : [],
          rating: typeof data.rating === 'number' ? data.rating : 0
        };
        
        // Set initial product data
        setProduct(productData);
        
        // Try to fetch reviews from subcollection (these take precedence)
        console.log("Fetching reviews from subcollection");
        try {
          const reviewsCollectionRef = collection(db, "Product", productId, "Reviews");
          const reviewsQuery = query(reviewsCollectionRef, orderBy("timestamp", "desc"));
          const reviewsSnapshot = await getDocs(reviewsQuery);
          
          if (!reviewsSnapshot.empty) {
            // We have reviews in the subcollection
            console.log(`Found ${reviewsSnapshot.size} reviews in subcollection`);
            const subcollectionReviews = [];
            
            reviewsSnapshot.forEach(doc => {
              const review = doc.data();
              // Add doc ID for reference
              subcollectionReviews.push({
                ...review,
                id: doc.id
              });
            });
            
            // Use these reviews instead of the ones in the product document
            setReviews(subcollectionReviews);
            
            // Check if the user has already reviewed
            if (user) {
              const userReview = subcollectionReviews.find(review => review.userId === user.uid);
              setHasReviewed(!!userReview);
            }
            
            // Fetch usernames for these reviews
            fetchUsernames(subcollectionReviews);
          } else {
            // No reviews in subcollection, fall back to document reviews
            console.log("No reviews in subcollection, using document reviews");
            setReviews(productData.reviews);
            
            // Check if the user has already reviewed based on document reviews
            if (user && productData.reviews.length > 0) {
              const userReview = productData.reviews.find(review => review.userId === user.uid);
              setHasReviewed(!!userReview);
            }
            
            // Fetch usernames for document reviews
            if (productData.reviews.length > 0) {
              fetchUsernames(productData.reviews);
            }
          }
        } catch (subcollectionError) {
          console.error("Error fetching reviews from subcollection:", subcollectionError);
          // Fall back to document reviews
          setReviews(productData.reviews);
          
          if (user && productData.reviews.length > 0) {
            const userReview = productData.reviews.find(review => review.userId === user.uid);
            setHasReviewed(!!userReview);
          }
          
          // Fetch usernames
          if (productData.reviews.length > 0) {
            console.log("Found", productData.reviews.length, "reviews in document");
            fetchUsernames(productData.reviews);
          }
        }
        
        // Set average rating from product document
        setAverageRating(productData.rating);
      } else {
        console.error("Product not found in database:", productId);
      }
    } catch (error) {
      console.error("Error fetching product:", error);
    }
  };

  const validateAndFixProductStructure = async () => {
    if (!productId) return;
    
    try {
      console.log("Validating product structure for ID:", productId);
      const productRef = doc(db, "Product", productId);
      const productSnap = await getDoc(productRef);
      
      if (!productSnap.exists()) {
        console.error("Product not found, cannot validate");
        return;
      }
      
      const data = productSnap.data();
      let needsUpdate = false;
      const updates = {};
      
      // Check if reviews field exists and is an array
      if (!data.hasOwnProperty('reviews')) {
        console.log("Product missing reviews field, will initialize");
        updates.reviews = [];
        needsUpdate = true;
      } else if (!Array.isArray(data.reviews)) {
        console.log("Product reviews field is not an array, will fix");
        updates.reviews = [];
        needsUpdate = true;
      }
      
      // Check if rating field exists and is a number
      if (!data.hasOwnProperty('rating')) {
        console.log("Product missing rating field, will initialize");
        updates.rating = 0;
        needsUpdate = true;
      } else if (typeof data.rating !== 'number') {
        console.log("Product rating field is not a number, will fix");
        updates.rating = 0;
        needsUpdate = true;
      }
      
      // If any issues were found, update the document
      if (needsUpdate) {
        console.log("Fixing product structure with updates:", updates);
        await setDoc(productRef, updates, { merge: true });
        console.log("Product structure fixed successfully");
        
        // Refresh product data
        fetchProduct();
      } else {
        console.log("Product structure is valid, no fixes needed");
      }
    } catch (error) {
      console.error("Error validating product structure:", error);
    }
  };

  useEffect(() => {
    if (productId) {
      fetchProduct();
      validateAndFixProductStructure();
    }
  }, [productId]);

  const fetchUsernames = async (reviewList) => {
    if (!reviewList || reviewList.length === 0) return;
    
    console.log("Fetching usernames for", reviewList.length, "reviews");
    const updatedUsernames = { ...usernames };
    const fetchPromises = [];
    
    for (const review of reviewList) {
      if (review.userId && !updatedUsernames[review.userId]) {
        // Queue up all username fetches to run in parallel
        fetchPromises.push(
          getDoc(doc(db, "User", review.userId))
            .then(userDoc => {
              if (userDoc.exists()) {
                updatedUsernames[review.userId] = userDoc.data().username || "User";
              } else {
                updatedUsernames[review.userId] = "Anonymous";
              }
            })
            .catch(error => {
              console.error("Error fetching username:", error);
              updatedUsernames[review.userId] = "Anonymous";
            })
        );
      }
    }
    
    // Wait for all username fetches to complete
    if (fetchPromises.length > 0) {
      try {
        await Promise.all(fetchPromises);
        console.log("Usernames fetched successfully:", updatedUsernames);
        setUsernames(updatedUsernames);
      } catch (error) {
        console.error("Error fetching usernames:", error);
      }
    }
  };

  const addReviewWithTransaction = async (reviewData, currentRating, reviewsCount) => {
    console.log("Attempting to add review with transaction");
    try {
      const productRef = doc(db, "Product", productId);
      
      await runTransaction(db, async (transaction) => {
        const productDoc = await transaction.get(productRef);
        
        if (!productDoc.exists()) {
          throw new Error("Product document does not exist!");
        }
        
        const productData = productDoc.data();
        const currentReviews = Array.isArray(productData.reviews) ? productData.reviews : [];
        const updatedReviews = [...currentReviews, reviewData];
        
        // Calculate new rating
        const totalRating = reviewsCount === 0 
          ? reviewData.rating 
          : currentRating * reviewsCount + reviewData.rating;
        const newAverage = totalRating / (reviewsCount + 1);
        
        transaction.update(productRef, {
          reviews: updatedReviews,
          rating: parseFloat(newAverage.toFixed(1))
        });
      });
      
      console.log("Review added successfully with transaction");
      return true;
    } catch (error) {
      console.error("Transaction failed:", error);
      throw error;
    }
  };

  const addReviewToSubcollection = async (reviewData) => {
    console.log("Attempting to add review to subcollection");
    try {
      // Add review to a reviews subcollection instead
      const reviewsCollectionRef = collection(db, "Product", productId, "Reviews");
      await addDoc(reviewsCollectionRef, reviewData);
      
      // Update the product rating
      const reviewsQuery = query(reviewsCollectionRef, orderBy("timestamp", "desc"));
      const reviewsSnapshot = await getDocs(reviewsQuery);
      
      let totalRating = 0;
      const allReviews = [];
      
      reviewsSnapshot.forEach(doc => {
        const review = doc.data();
        totalRating += review.rating || 0;
        allReviews.push(review);
      });
      
      const newRating = allReviews.length > 0 ? totalRating / allReviews.length : 0;
      
      // Update product with new rating
      const productRef = doc(db, "Product", productId);
      await updateDoc(productRef, {
        rating: parseFloat(newRating.toFixed(1))
      });
      
      console.log("Review added to subcollection successfully");
      return allReviews;
    } catch (error) {
      console.error("Error adding review to subcollection:", error);
      throw error;
    }
  };

  const handleSubmitReview = async () => {
    if (!user) {
      setReviewError("You must be logged in to leave a review.");
      return;
    }

    if (reviewText.trim() === "") {
      setReviewError("Please write something in your review.");
      return;
    }

    if (hasReviewed) {
      setReviewError("You have already reviewed this product.");
      return;
    }

    // Check if user has UID
    if (!user.uid) {
      setReviewError("User authentication issue. Please log out and log back in.");
      return;
    }

    // Check if user is verified
    if (!user.emailVerified) {
      if (!checkVerification(navigate)) {
        setReviewError("Email verification required to submit reviews.");
        return;
      }
    }

    // Check if product exists
    if (!product || !product.id) {
      setReviewError("Product information is missing. Please refresh the page.");
      return;
    }

    setReviewError("");
    setSubmittingReview(true);

    // Define a simple review object for UI updates
    const simpleReview = {
      text: reviewText,
      rating: selectedRating,
      userId: user.uid,
      timestamp: new Date().toISOString()
    };

    try {
      // First try the subcollection approach as it's most likely to work
      console.log("Starting with subcollection approach");
      try {
        const allReviews = await addReviewToSubcollection({
          text: reviewText,
          rating: selectedRating,
          userId: user.uid,
          timestamp: serverTimestamp()
        });

        // If this succeeds, update the UI and return early
        console.log("Review added successfully via subcollection");
        
        if (allReviews && allReviews.length > 0) {
          setReviews(allReviews);
        } else {
          // Just update local state with the new review
          setReviews(prev => [...prev, simpleReview]);
        }
        
        // Update UI state
        setAverageRating(prevRating => {
          const total = (prevRating * reviews.length) + selectedRating;
          return (total / (reviews.length + 1)).toFixed(1);
        });
        
        setReviewText("");
        setSelectedRating(5);
        setReviewSubmitted(true);
        setHasReviewed(true);
        
        // Update usernames
        setUsernames(prev => ({
          ...prev,
          [user.uid]: user.displayName || "User"
        }));
        
        setTimeout(() => {
          setReviewSubmitted(false);
        }, 3000);
        
        return; // Return early if successful
      } catch (subcollectionError) {
        console.error("Subcollection approach failed:", subcollectionError);
        // Continue to try other approaches
      }

      // ... rest of the existing code with other approaches ...
    } catch (error) {
      console.error("All review submission approaches failed:", error);
      setReviewError("Failed to submit review: " + (error.message || "Unknown error"));
    } finally {
      setSubmittingReview(false);
    }
  };

  // Add a new useEffect to refresh reviews when user changes or component mounts
  useEffect(() => {
    // Only fetch if we have both productId and user
    if (productId && user) {
      console.log("User or focus changed, refreshing reviews");
      fetchProduct(); // Re-fetch product and reviews
    }
  }, [user]); // Only re-run when user changes

  // Also update the formatDate function to show more detailed timestamps
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    
    try {
      let date;
      
      // Handle Firestore timestamp
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Handle ISO string
      else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      }
      // Handle JavaScript Date object
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Handle seconds/milliseconds numeric timestamp
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else {
        return "Invalid date";
      }
      
      // Format date with time
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid date";
    }
  };

  // Add a new function to manually refresh reviews
  const refreshReviews = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      console.log("Manually refreshing reviews");
      await fetchProduct();
    } catch (error) {
      console.error("Error refreshing reviews:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="product-details-container">
      <div className="product-top">
        <div className="product-gallery">
          <img
            src={product?.images?.[0] || "https://via.placeholder.com/300"}
            alt={product?.title || product?.name}
          />
        </div>

        <div className="product-info">
          <h1 className="product-title">{product?.title || product?.name}</h1>
          <div className="rating-section">
            <span className="average-rating">⭐ {averageRating}</span>
            <span className="reviews-count">({reviews.length} reviews)</span>
          </div>
          <p className="product-price">
            AED {product?.price} <span className="previous-price">{product?.previousPrice}</span>
          </p>
          {product?.description && (
            <p className="product-card__description">{product.description}</p>
          )}
          
          <div className="product-buttons">
            {product?.ownerID && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/MyAccount", { state: { profileUserId: product.ownerID } });
                }}
                className="product-seller-button"
              >
                View Seller Profile
              </button>
            )}

            <button className={`product-seller-button ${liked ? 'liked' : ''}`} onClick={handleLikeToggle}>
              {liked ? "❤️ Liked" : "🤍 Like"}
            </button>
            
            <button 
              className={`product-seller-button add-cart ${user && !isVerified ? 'disabled-cart' : ''}`}
              onClick={handleAddToCart}
              disabled={user && !isVerified}
            >
              {user && !isVerified ? "Verify Email to Shop" : "🛒 Add to Cart"}
            </button>
          </div>
          
          {user && !isVerified && (
            <div className="verification-warning cart-verification">
              <p>Email verification required to add products to cart</p>
              <button 
                onClick={() => checkVerification(navigate)}
                className="verify-email-button"
              >
                Verify Email
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="comments-section">
        <div className="reviews-header">
          <h3>Reviews</h3>
          <button 
            className="refresh-reviews-button" 
            onClick={refreshReviews}
            disabled={isRefreshing}
          >
            {isRefreshing ? "Refreshing..." : "🔄 Refresh"}
          </button>
        </div>
        {reviews.length > 0 ? (
          <ul className="comments-list">
            {reviews.map((review, index) => (
              <li key={review.id || index} className="review-item">
                <div className="review-header">
                  <strong
                    className="review-username"
                    onClick={() => navigate("/MyAccount", { state: { profileUserId: review.userId } })}
                  >
                    {usernames[review.userId] || "Anonymous"}
                  </strong>
                  <span className="review-rating">
                    {Array(review.rating || 5).fill('⭐').join('')}
                  </span>
                  {review.timestamp && (
                    <span className="review-date">{formatDate(review.timestamp)}</span>
                  )}
                </div>
                <div className="review-content">{review.text}</div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="no-comments">No reviews yet. Be the first to share your thoughts!</p>
        )}

        {user && !hasReviewed ? (
          <div className="write-review">
            <h4>Write a Review</h4>
            
            {!user.emailVerified && (
              <div className="verification-warning review-verification">
                <p>Email verification required to submit reviews</p>
                <button 
                  onClick={() => checkVerification(navigate)}
                  className="verify-email-button"
                >
                  Verify Email
                </button>
              </div>
            )}
            
            <div className="rating-selector">
              <label htmlFor="rating-select">Your Rating:</label>
              <div className="star-rating">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span 
                    key={star} 
                    className={`star ${star <= selectedRating ? 'selected' : ''}`}
                    onClick={() => setSelectedRating(star)}
                  >
                    ⭐
                  </span>
                ))}
              </div>
              <select
                id="rating-select"
                value={selectedRating}
                onChange={(e) => setSelectedRating(Number(e.target.value))}
              >
                <option value="5">⭐⭐⭐⭐⭐ (5 Stars)</option>
                <option value="4">⭐⭐⭐⭐ (4 Stars)</option>
                <option value="3">⭐⭐⭐ (3 Stars)</option>
                <option value="2">⭐⭐ (2 Stars)</option>
                <option value="1">⭐ (1 Star)</option>
              </select>
            </div>
            
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share your thoughts about this product..."
              className={`review-textarea ${reviewError && !reviewText ? "error" : ""}`}
              minLength={3}
              maxLength={500}
              required
            ></textarea>
            
            {reviewError && <p className="review-error">{reviewError}</p>}
            {reviewSubmitted && <p className="review-success">Review submitted successfully!</p>}
            
            <button 
              onClick={handleSubmitReview} 
              disabled={submittingReview || !reviewText.trim() || !user.emailVerified}
              className="submit-review-button"
            >
              {submittingReview ? "Submitting..." : user.emailVerified ? "Submit Review" : "Verify Email to Review"}
            </button>
          </div>
        ) : user && hasReviewed ? (
          <div className="already-reviewed">
            <p>You've already reviewed this product. Thank you for your feedback!</p>
          </div>
        ) : (
          <div className="login-to-review">
            <p>Please <span onClick={() => navigate("/login")} className="login-link">login</span> to leave a review.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductDetails;
